"use client";

import { useState } from "react";
import { useLocation } from "react-router-dom";
import { LanguageLink } from "@/components/LanguageLink";
import { AnalyticsLanguageLink } from "@/components/AnalyticsLanguageLink";
import { getLanguageFromPath, addLanguageToPath } from "@/lib/language";
import { useClerk, UserButton } from "@clerk/clerk-react";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import logo from "@/assets/images/logo.svg";
import { useTranslation } from "react-i18next";
import { LanguageToggle } from "@/components/ui/language-toggle";
import { LanguagesIcon } from "lucide-react";

interface DashboardHeaderProps extends React.HTMLAttributes<HTMLElement> {}

function useNavLinks() {
  const { t } = useTranslation();
  return [
    { label: t("dashboardHeader.nav.createVideo"), href: "/dashboard" },
    { label: t("dashboardHeader.nav.myVideos"), href: "/videos" },
    { label: t("dashboardHeader.nav.myAccount"), href: "/account" },
  ];
}

function DashboardHeader({ className, ...props }: DashboardHeaderProps) {
  const { signOut } = useClerk();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const { t } = useTranslation();
  const { i18n } = useTranslation();

  const location = useLocation();
  const navLinks = useNavLinks();
  const currentLang = i18n.language?.startsWith("sv") ? "sv" : "en";

  // Get language-aware redirect URL for logout
  const currentLanguage = getLanguageFromPath(location.pathname);
  const logoutRedirectUrl = addLanguageToPath("/", currentLanguage);

  const toggleLanguage = () => {
    const newLang = currentLang === "sv" ? "en" : "sv";
    i18n.changeLanguage(newLang);
  };

  const handleSignOut = () => {
    signOut({ redirectUrl: logoutRedirectUrl });
  };

  const toggleMobileMenu = () => {
    setIsMobileMenuOpen(!isMobileMenuOpen);
  };

  const closeMobileMenu = () => {
    setIsMobileMenuOpen(false);
  };

  return (
    <>
      <header
        className={cn(
          "flex items-center justify-between pt-4 pb-8 w-full gap-4",
          "md:pb-20 md:gap-12",
          "2k:pt-14 2k:pb-32 2k:gap-24",
          "4k:pt-14 4k:pb-42 4k:gap-28",
          className
        )}
        {...props}
      >
        {/* Logo */}
        <LanguageLink to="/" className="flex items-center shrink-0">
          <img src={logo} alt="Logo" className="h-10 md:h-12 2k:h-18 4k:h-32" />
        </LanguageLink>

        {/* Desktop Navigation Links */}
        <nav 
          className={cn(
            "hidden md:flex md:flex-1 items-center justify-end gap-8",
            "lg:gap-12",
            "2k:gap-20",
            "4k:gap-28",
          )}
        >
          {navLinks.map((link) => {
            const isActive = location.pathname === link.href;
            return (
              <AnalyticsLanguageLink
                key={link.href}
                to={link.href}
                className={cn(
                  "font-link transition-colors",
                  isActive
                    ? "text-foreground underline decoration-2 underline-offset-6 2k:decoration-2 4k:decoration-4"
                    : "hover:underline decoration-0 hover:decoration-2 underline-offset-2 hover:underline-offset-6 transition-all duration-100 ease-in-out 4k:hover:decoration-4",
                )}
              >
                {link.label}
              </AnalyticsLanguageLink>
            );
          })}
        </nav>

        {/* Mobile Menu Button */}
        <button
          onClick={toggleMobileMenu}
          className="md:hidden flex flex-col gap-1.5 p-2 shrink-0"
          aria-label="Toggle menu"
        >
          <span className={cn(
            "w-6 h-0.5 bg-foreground transition-all duration-300",
            isMobileMenuOpen && "rotate-45 translate-y-2"
          )} />
          <span className={cn(
            "w-6 h-0.5 bg-foreground transition-all duration-300",
            isMobileMenuOpen && "opacity-0"
          )} />
          <span className={cn(
            "w-6 h-0.5 bg-foreground transition-all duration-300",
            isMobileMenuOpen && "-rotate-45 -translate-y-2"
          )} />
        </button>

        {/* Desktop User Section */}
        <div className="hidden md:flex items-center gap-2 md:gap-6 2k:gap-12 4k:gap-16">
          <UserButton afterSignOutUrl={logoutRedirectUrl}>
            <UserButton.MenuItems>
              <UserButton.Action
                label={currentLang === "sv" ? t("dashboardHeader.switchToEnglish") : t("dashboardHeader.switchToSwedish")}
                labelIcon={<LanguagesIcon className="size-4" />}
                onClick={toggleLanguage}
              />
            </UserButton.MenuItems>
          </UserButton>
          <Button
            variant="default"
            className={cn(
            "rounded-sm bg-black hover:bg-black/90 font-link text-white px-3 py-4 cursor-pointer text-sm",
            "md:px-4 md:py-5.5 md:text-base",
            "2k:px-6 2k:py-9 2k:rounded-md",
            "4k:px-12 4k:py-14 4k:rounded-xl"
          )}
            onClick={handleSignOut}
          >
            {t("common.logout")}
          </Button>
        </div>
      </header>

      {/* Mobile Menu Overlay */}
      {isMobileMenuOpen && (
        <div
          className="md:hidden fixed inset-0 bg-black/50 z-40"
          onClick={closeMobileMenu}
        />
      )}

      {/* Mobile Menu Drawer */}
      <div
        className={cn(
          "md:hidden fixed top-0 right-0 h-full w-64 bg-background shadow-lg z-50 transition-transform duration-300 ease-in-out",
          isMobileMenuOpen ? "translate-x-0" : "translate-x-full"
        )}
      >
        <div className="flex flex-col h-full p-6">
          {/* Close Button */}
          <button
            onClick={closeMobileMenu}
            className="self-end p-2 mb-8"
            aria-label="Close menu"
          >
            <svg
              className="w-6 h-6"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M6 18L18 6M6 6l12 12"
              />
            </svg>
          </button>

          {/* Mobile Navigation Links */}
          <nav className="flex flex-col gap-6 mb-8">
            {navLinks.map((link) => {
              const isActive = location.pathname === link.href;
              return (
                <AnalyticsLanguageLink
                  key={link.href}
                  to={link.href}
                  onClick={closeMobileMenu}
                  className={cn(
                    "font-link text-lg transition-colors",
                    isActive
                      ? "text-foreground underline decoration-2 underline-offset-6"
                      : "text-foreground/80 hover:text-foreground",
                  )}
                >
                  {link.label}
                </AnalyticsLanguageLink>
              );
            })}
          </nav>

          {/* Mobile User Section */}
          <div className="mt-auto flex flex-col gap-4">
            <div className="flex items-center gap-3 pb-4 border-b border-border">
              <UserButton afterSignOutUrl={logoutRedirectUrl} />
              <span className="font-link text-sm">{t("dashboardHeader.nav.myAccount")}</span>
            </div>
            <LanguageToggle className="border border-black bg-transparent text-black w-full" />
            <Button
              variant="default"
              className="rounded-sm bg-black hover:bg-black/90 font-link text-white px-4 py-4 cursor-pointer w-full"
              onClick={() => {
                handleSignOut();
                closeMobileMenu();
              }}
            >
              {t("common.logout")}
            </Button>
          </div>
        </div>
      </div>
    </>
  );
}

export { DashboardHeader };
